import { useCallback, useEffect, useState } from "react";

interface UseVideoPlaybackProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onPlayChange?: (isPlaying: boolean) => void;
}

export const useVideoPlayback = ({ videoRef, onPlayChange }: UseVideoPlaybackProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedIndicator, setShowSpeedIndicator] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackSpeed;
  }, [playbackSpeed, videoRef]);

  const togglePlay = useCallback(
    (event?: React.MouseEvent | React.TouchEvent | KeyboardEvent) => {
      event?.stopPropagation?.();
      const video = videoRef.current;
      if (!video) return;

      if (video.paused) {
        video
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      } else {
        video.pause();
        setIsPlaying(false);
      }
      onPlayChange?.(true);
    },
    [videoRef, onPlayChange]
  );

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    onPlayChange?.(true);
  }, [onPlayChange]);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
    onPlayChange?.(false);
  }, [onPlayChange]);

  const handleSpeedChange = useCallback(
    (speed: number) => {
      setPlaybackSpeed(speed);
      onPlayChange?.(true); // Ensure controls show up or reset timeout
    },
    [onPlayChange]
  );

  const enableDoubleSpeed = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = 2;
    setShowSpeedIndicator(true);
  }, [videoRef]);

  const disableDoubleSpeed = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = playbackSpeed;
    setShowSpeedIndicator(false);
  }, [playbackSpeed, videoRef]);

  return {
    isPlaying,
    playbackSpeed,
    showSpeedIndicator,
    togglePlay,
    handleVideoPlay,
    handleVideoPause,
    handleSpeedChange,
    enableDoubleSpeed,
    disableDoubleSpeed,
  };
};
